import React, { useState, useEffect, useMemo } from 'react';
import { getModels } from '../services/modelService';
import { ModelSyncStatus } from '../components/models/ModelSyncStatus';
import { ModelPriceTrends } from '../components/models/ModelPriceTrends';
import { ModelComparison } from '../components/models/ModelComparison';
import { useTheme } from '../contexts/ThemeContext';
import { Spinner } from '../components/common/Spinner';
import { Range } from 'react-range';

// Helper function to format dollars per million tokens
const formatDollars = (millicents: number) => {
  // Convert from millicents per 1K tokens to dollars per 1M tokens
  // millicents per 1K tokens * 1000 = millicents per 1M tokens
  // millicents / 100,000 = dollars
  const dollarsPerMillion = millicents * 0.01;
  
  // Format based on the value range, preserving negative signs
  if (dollarsPerMillion === 0) {
    return '$0.00';
  } else if (Math.abs(dollarsPerMillion) < 0.01) {
    return `${dollarsPerMillion < 0 ? '-' : ''}$${Math.abs(dollarsPerMillion).toFixed(6)}`;  // Show more decimal places for very small values
  } else if (Math.abs(dollarsPerMillion) < 1) {
    return `${dollarsPerMillion < 0 ? '-' : ''}$${Math.abs(dollarsPerMillion).toFixed(4)}`;  // Show 4 decimal places for small values
  } else {
    return `${dollarsPerMillion < 0 ? '-' : ''}$${Math.abs(dollarsPerMillion).toFixed(2)}`;  // Show 2 decimal places for larger values
  }
};

// Helper function to export model data to CSV
const exportToCSV = (data: any[], filename: string) => {
  // Define the columns we want to export
  const columns = [
    'provider', 
    'name', 
    'id', 
    'context_length', 
    'prompt_cost_per_1k', 
    'completion_cost_per_1k', 
    'is_available'
  ];
  
  // Create header row with formatted column names
  const headerRow = columns.map(col => {
    // Format column names for better readability
    switch(col) {
      case 'prompt_cost_per_1k': return 'Input Price (per 1M tokens)';
      case 'completion_cost_per_1k': return 'Output Price (per 1M tokens)';
      case 'context_length': return 'Context Length (tokens)';
      case 'is_available': return 'Available';
      default: return col.charAt(0).toUpperCase() + col.slice(1).replace('_', ' ');
    }
  }).join(',');
  
  // Format each row of data
  const rows = data.map(model => {
    return columns.map(col => {
      // Format specific columns
      if (col === 'prompt_cost_per_1k' || col === 'completion_cost_per_1k') {
        // Remove $ sign for CSV
        return formatDollars(model[col]).replace('$', '');
      } else if (col === 'is_available') {
        return model[col] ? 'Yes' : 'No';
      } else if (col === 'context_length') {
        return model[col].toString();
      } else {
        // Wrap text fields in quotes to handle commas
        return typeof model[col] === 'string' ? `"${model[col]}"` : model[col];
      }
    }).join(',');
  });
  
  // Combine header and rows
  const csv = [headerRow, ...rows].join('\n');
  
  // Create a blob and download link
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Helper function to get a consistent color for each provider
const getProviderColor = (provider: string): string => {
  // Map of provider names to tailwind color classes
  const colorMap: Record<string, string> = {
    'openai': 'bg-green-500',
    'anthropic': 'bg-blue-500',
    'google': 'bg-red-500',
    'mistral': 'bg-purple-500',
    'cohere': 'bg-yellow-500',
    'azure': 'bg-blue-400',
    'openrouter': 'bg-orange-500',
    'replicate': 'bg-indigo-500',
    'huggingface': 'bg-pink-500',
    'aws': 'bg-yellow-600',
    'local': 'bg-gray-500'
  };
  
  // Return the mapped color or a default color if provider is not in the map
  return colorMap[provider.toLowerCase()] || 'bg-gray-400';
};

const ModelDashboard: React.FC = () => {
  const [models, setModels] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'comparison' | 'trends' | 'sync' | 'prices'>('overview');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortField, setSortField] = useState<string>('provider');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterProvider, setFilterProvider] = useState<string>('all');
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [capabilityFilters, setCapabilityFilters] = useState<string[]>([]);
  const [selectedModelDetail, setSelectedModelDetail] = useState<any>(null);
  
  // Function to reset capability filters
  const resetCapabilityFilters = () => {
    setCapabilityFilters([]);
    // Only set isFilterActive to false if no other filters are active
    if (!searchQuery && 
        contextRange[0] === contextMinMax[0] && 
        contextRange[1] === contextMinMax[1] &&
        priceRange[0] === priceMinMax[0] && 
        priceRange[1] === priceMinMax[1] &&
        outputPriceRange[0] === outputPriceMinMax[0] && 
        outputPriceRange[1] === outputPriceMinMax[1]) {
      setIsFilterActive(false);
    }
  };
  
  // Function to reset all filters
  const resetAllFilters = () => {
    setCapabilityFilters([]);
    setContextRange(contextMinMax);
    setPriceRange(priceMinMax);
    setOutputPriceRange(outputPriceMinMax);
    setSearchQuery('');
    setFilterProvider('all');
    setIsFilterActive(false);
    
    // Force a re-render by setting a temporary loading state
    setLoading(true);
    setTimeout(() => setLoading(false), 100);
  };
  const [contextRange, setContextRange] = useState<[number, number]>([0, 0]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 0]);
  const [outputPriceRange, setOutputPriceRange] = useState<[number, number]>([0, 0]);
  const [isFilterActive, setIsFilterActive] = useState<boolean>(false);
  const [contextInputMin, setContextInputMin] = useState<string>('');
  const [contextInputMax, setContextInputMax] = useState<string>('');
  const [priceInputMin, setPriceInputMin] = useState<string>('');
  const [priceInputMax, setPriceInputMax] = useState<string>('');
  const [outputPriceInputMin, setOutputPriceInputMin] = useState<string>('');
  const [outputPriceInputMax, setOutputPriceInputMax] = useState<string>('');
  const [isProviderSectionExpanded, setIsProviderSectionExpanded] = useState<boolean>(false);
  const [isCapabilitySectionExpanded, setIsCapabilitySectionExpanded] = useState<boolean>(false);
  const { darkMode } = useTheme();
  
  useEffect(() => {
    fetchModels();
  }, []);
  
  const fetchModels = async () => {
    setLoading(true);
    try {
      const data = await getModels();
      setModels(data);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching models:", err);
      setError(err.message || 'Failed to fetch models');
    } finally {
      setLoading(false);
    }
  };

  // Get unique providers for filter dropdown
  const providers = useMemo(() => {
    const uniqueProviders = new Set(models.map(model => model.provider));
    return ['all', ...Array.from(uniqueProviders)];
  }, [models]);

  // Check for negative prices (excluding openrouter/auto which is expected to have negative prices)
  const hasNegativePrices = useMemo(() => {
    return models.some(model => 
      (model.prompt_cost_per_1k < 0 || model.completion_cost_per_1k < 0) && 
      model.id !== 'openrouter/auto'
    );
  }, [models]);
  
  // Filter out negative prices and openrouter/auto for price calculations
  const validPriceModels = useMemo(() => {
    return models.filter(model => 
      model.prompt_cost_per_1k >= 0 && 
      model.completion_cost_per_1k >= 0 &&
      model.id !== 'openrouter/auto'
    );
  }, [models]);
  
  // Calculate min/max values for context and price ranges
  const contextMinMax = useMemo(() => {
    if (models.length === 0) return [0, 0];
    const min = Math.min(...models.map(m => m.context_length));
    const max = Math.max(...models.map(m => m.context_length));
    return [min, max];
  }, [models]);

  const priceMinMax = useMemo(() => {
    if (validPriceModels.length === 0) return [0, 0];
    const min = Math.min(...validPriceModels.map(m => m.prompt_cost_per_1k));
    const max = Math.max(...validPriceModels.map(m => m.prompt_cost_per_1k));
    return [min, max];
  }, [validPriceModels]);

  const outputPriceMinMax = useMemo(() => {
    if (validPriceModels.length === 0) return [0, 0];
    const min = Math.min(...validPriceModels.map(m => m.completion_cost_per_1k));
    const max = Math.max(...validPriceModels.map(m => m.completion_cost_per_1k));
    return [min, max];
  }, [validPriceModels]);

  // Initialize ranges when models are loaded
  useEffect(() => {
    if (contextMinMax[1] > contextMinMax[0]) {
      // Ensure values are aligned with step
      const min = Math.round((contextMinMax[0] - contextMinMax[0]) / 1000) * 1000 + contextMinMax[0];
      const max = Math.round((contextMinMax[1] - contextMinMax[0]) / 1000) * 1000 + contextMinMax[0];
      setContextRange([min, max]);
    }
    if (priceMinMax[1] > priceMinMax[0]) {
      // Ensure values are aligned with step
      const min = Math.floor(priceMinMax[0]);
      const max = Math.ceil(priceMinMax[1]);
      setPriceRange([min, max]);
    }
    if (outputPriceMinMax[1] > outputPriceMinMax[0]) {
      // Ensure values are aligned with step
      const min = Math.floor(outputPriceMinMax[0]);
      const max = Math.ceil(outputPriceMinMax[1]);
      setOutputPriceRange([min, max]);
    }
  }, [contextMinMax, priceMinMax, outputPriceMinMax]);
  
  // Update input values when range changes
  useEffect(() => {
    setContextInputMin(contextRange[0].toString());
    setContextInputMax(contextRange[1].toString());
  }, [contextRange]);

  useEffect(() => {
    setPriceInputMin(formatDollarInput(priceRange[0]));
    setPriceInputMax(formatDollarInput(priceRange[1]));
  }, [priceRange]);
  
  useEffect(() => {
    setOutputPriceInputMin(formatDollarInput(outputPriceRange[0]));
    setOutputPriceInputMax(formatDollarInput(outputPriceRange[1]));
  }, [outputPriceRange]);
  
  // Format and parse dollar values
  const formatDollarInput = (value: number): string => {
    // Convert from millicents per 1K tokens to dollars per 1M tokens
    // millicents per 1K tokens * 1000 = millicents per 1M tokens
    // millicents / 100,000 = dollars
    const dollarsPerMillion = value * 0.01;
    
    return `$${dollarsPerMillion.toFixed(2)}`;
  };

  const parseDollarInput = (value: string): number => {
    // Remove $ and any commas, then parse as float
    const dollarsPerMillion = parseFloat(value.replace(/[$,]/g, '')) || 0;
    
    // Convert back to millicents per 1K tokens
    // dollars * 100,000 = millicents per 1M tokens
    // millicents per 1M tokens / 1000 = millicents per 1K tokens
    return dollarsPerMillion / 0.01;
  };

  // Handle input changes
  const handleContextMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContextInputMin(e.target.value);
  };

  const handleContextMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContextInputMax(e.target.value);
  };

  const handlePriceMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow typing without $ for easier input
    setPriceInputMin(e.target.value);
  };

  const handlePriceMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow typing without $ for easier input
    setPriceInputMax(e.target.value);
  };

  // Handle form submissions
  const handleContextRangeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const min = parseInt(contextInputMin);
    const max = parseInt(contextInputMax);
    
    if (!isNaN(min) && !isNaN(max) && min <= max) {
      // Ensure values are within bounds
      const boundedMin = Math.max(min, contextMinMax[0]);
      const boundedMax = Math.min(max, contextMinMax[1]);
      
      setContextRange([boundedMin, boundedMax]);
      setIsFilterActive(true);
    }
  };

  const handlePriceRangeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse the input values, removing any $ signs
    const min = parseDollarInput(priceInputMin);
    const max = parseDollarInput(priceInputMax);
    
    if (!isNaN(min) && !isNaN(max) && min <= max) {
      // Ensure values are within bounds
      const boundedMin = Math.max(min, priceMinMax[0]);
      const boundedMax = Math.min(max, priceMinMax[1]);
      
      setPriceRange([boundedMin, boundedMax]);
      setIsFilterActive(true);
      
      // Format the input values properly after submission
      setPriceInputMin(formatDollarInput(boundedMin));
      setPriceInputMax(formatDollarInput(boundedMax));
    }
  };

  const handleOutputPriceMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow typing without $ for easier input
    setOutputPriceInputMin(e.target.value);
  };

  const handleOutputPriceMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow typing without $ for easier input
    setOutputPriceInputMax(e.target.value);
  };

  const handleOutputPriceRangeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse the input values, removing any $ signs
    const min = parseDollarInput(outputPriceInputMin);
    const max = parseDollarInput(outputPriceInputMax);
    
    if (!isNaN(min) && !isNaN(max) && min <= max) {
      // Ensure values are within bounds
      const boundedMin = Math.max(min, outputPriceMinMax[0]);
      const boundedMax = Math.min(max, outputPriceMinMax[1]);
      
      setOutputPriceRange([boundedMin, boundedMax]);
      setIsFilterActive(true);
      
      // Format the input values properly after submission
      setOutputPriceInputMin(formatDollarInput(boundedMin));
      setOutputPriceInputMax(formatDollarInput(boundedMax));
    }
  };

  // Handle export in different formats
  const handleExport = (format: 'csv' | 'xlsx' | 'md') => {
    const now = new Date();
    const dateStr = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    
    if (format === 'csv') {
      exportToCSV(sortedModels, `model_metadata_${dateStr}.csv`);
    }
    // Future implementations for xlsx and md would go here
    
    // Close the modal after export
    setShowExportModal(false);
  };

  // Sort and filter models
  const sortedModels = useMemo(() => {
    let filteredModels = [...models];
    
    // Apply provider filter
    if (filterProvider !== 'all') {
      filteredModels = filteredModels.filter(model => model.provider === filterProvider);
    }
    
    // Apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredModels = filteredModels.filter(model => 
        model.name.toLowerCase().includes(query) || 
        model.id.toLowerCase().includes(query) ||
        (model.description && model.description.toLowerCase().includes(query))
      );
    }
    
    // Apply capability filters
    if (capabilityFilters.length > 0) {
      console.log('Applying capability filters:', capabilityFilters);
      console.log('Models before capability filtering:', filteredModels.length);
      
      filteredModels = filteredModels.filter(model => {
        // Check if model has capabilities property
        if (!model.capabilities || typeof model.capabilities !== 'object') {
          console.log('Model', model.id, 'has no valid capabilities object');
          return false;
        }
        
        // Check if model has ALL of the selected capabilities (more strict)
        const passes = capabilityFilters.every(capability => {
          const hasCapability = model.capabilities[capability] === true;
          return hasCapability;
        });
        
        return passes;
      });
      
      console.log('Models after capability filtering:', filteredModels.length);
    }
    
    // Apply context range filter if active
    if (isFilterActive && (contextRange[0] !== contextMinMax[0] || contextRange[1] !== contextMinMax[1])) {
      filteredModels = filteredModels.filter(model => 
        model.context_length >= contextRange[0] && model.context_length <= contextRange[1]
      );
    }
    
    // Apply price range filter if active
    if (isFilterActive && (priceRange[0] !== priceMinMax[0] || priceRange[1] !== priceMinMax[1])) {
      filteredModels = filteredModels.filter(model => 
        model.prompt_cost_per_1k >= priceRange[0] && model.prompt_cost_per_1k <= priceRange[1]
      );
    }
    
    // Apply output price range filter if active
    if (isFilterActive && (outputPriceRange[0] !== outputPriceMinMax[0] || outputPriceRange[1] !== outputPriceMinMax[1])) {
      filteredModels = filteredModels.filter(model => 
        model.completion_cost_per_1k >= outputPriceRange[0] && model.completion_cost_per_1k <= outputPriceRange[1]
      );
    }
    
    // Apply sorting
    const result = filteredModels.sort((a, b) => {
      // Handle nested properties like 'capabilities.tokenizer'
      let aValue, bValue;
      
      if (sortField.includes('.')) {
        // Handle nested properties
        const parts = sortField.split('.');
        let aProp = a;
        let bProp = b;
        
        for (const part of parts) {
          aProp = aProp && typeof aProp === 'object' ? aProp[part] : undefined;
          bProp = bProp && typeof bProp === 'object' ? bProp[part] : undefined;
        }
        
        aValue = aProp;
        bValue = bProp;
      } else {
        // Handle regular properties
        aValue = a[sortField];
        bValue = b[sortField];
      }
      
      // Handle undefined or null values
      if (aValue === undefined || aValue === null) return sortDirection === 'asc' ? -1 : 1;
      if (bValue === undefined || bValue === null) return sortDirection === 'asc' ? 1 : -1;
      
      // Handle numeric fields
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      
      // Handle boolean fields
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortDirection === 'asc' 
          ? (aValue === bValue ? 0 : aValue ? 1 : -1)
          : (aValue === bValue ? 0 : aValue ? -1 : 1);
      }
      
      return 0;
    });
    
    console.log('FINAL FILTERED MODELS:', result.length);
    return result;
  }, [models, sortField, sortDirection, filterProvider, searchQuery, capabilityFilters, isFilterActive, contextRange, priceRange, outputPriceRange, contextMinMax, priceMinMax, outputPriceMinMax]);

  // Handle sort click
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render sort indicator
  const renderSortIndicator = (field: string) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };
  
  // Render grid view
  const renderGridView = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedModels.map(model => (
          <div 
            key={model.id}
            className={`p-4 rounded-lg hover:shadow-md transition-shadow ${
              darkMode 
                ? (model.is_available ? 'bg-gray-800' : 'bg-gray-800 opacity-50') 
                : (model.is_available ? 'bg-white shadow' : 'bg-gray-100')
            } border`}
          >
            {/* Header with model name, provider badge, and status indicator */}
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-2 ${model.is_available ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <h3 className="font-bold truncate">{model.name}</h3>
              </div>
              <span className={`text-xs px-2 py-1 rounded flex items-center ${
                darkMode ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <div className={`w-2 h-2 rounded-full mr-1 ${getProviderColor(model.provider)}`}></div>
                {model.provider}
              </span>
            </div>
            
            {/* Description with truncation */}
            <p className={`text-sm mb-3 line-clamp-2 h-10 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {model.description || 'No description available'}
            </p>
            
            {/* Key specs in a grid */}
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div className="flex flex-col">
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Context</span>
                <span>{model.context_length.toLocaleString()}</span>
              </div>
              <div className="flex flex-col">
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Input</span>
                <span>{formatDollars(model.prompt_cost_per_1k)}/1M</span>
              </div>
              <div className="flex flex-col">
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Output</span>
                <span>{formatDollars(model.completion_cost_per_1k)}/1M</span>
              </div>
              <div className="flex flex-col">
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tokenizer</span>
                <span>{model.capabilities.tokenizer || 'Unknown'}</span>
              </div>
            </div>
            
            {/* Capabilities as tags */}
            <div className="mt-3">
              <div className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Capabilities</div>
              <div className="flex flex-wrap gap-1">
                {/* Show basic capabilities first */}
                {['vision', 'tools', 'json_mode', 'function_calling'].map(key => (
                  model.capabilities[key] && (
                    <span 
                      key={key}
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {key.replace('_', ' ')}
                    </span>
                  )
                ))}
                
                {/* Show input modalities */}
                {model.capabilities.input_modalities && model.capabilities.input_modalities.map(modality => (
                  <span 
                    key={`input-${modality}`}
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      darkMode ? 'bg-purple-900 text-purple-100' : 'bg-purple-100 text-purple-800'
                    }`}
                  >
                    {modality}
                  </span>
                ))}
              </div>
            </div>
            
            {/* View details button */}
            <div className="mt-3 text-right">
              <button 
                className={`text-xs px-2 py-1 rounded ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600 text-blue-300' : 'bg-gray-100 hover:bg-gray-200 text-blue-600'
                }`}
                onClick={() => setSelectedModelDetail(model)}
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Render list view (table)
  const renderListView = () => {
    return (
      <div className={`overflow-x-auto rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <table className={`w-full divide-y ${darkMode ? 'divide-gray-700 bg-gray-800 text-gray-200' : 'divide-gray-200 bg-white text-gray-800'}`}>
          <thead className={darkMode ? 'bg-gray-900' : 'bg-gray-50'}>
            <tr>
              {/* Actions column - moved to far left */}
              <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider">
                Actions
              </th>
              {/* Status column - moved to be after Actions */}
              <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider">
                Status
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer w-16" // Make provider column even thinner
                onClick={() => handleSort('provider')}
              >
                Provider{renderSortIndicator('provider')}
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer w-56" // Make model column even wider
                onClick={() => handleSort('name')}
              >
                Model{renderSortIndicator('name')}
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('context_length')}
              >
                Context{renderSortIndicator('context_length')}
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('prompt_cost_per_1k')}
              >
                Input{renderSortIndicator('prompt_cost_per_1k')}
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('completion_cost_per_1k')}
              >
                Output{renderSortIndicator('completion_cost_per_1k')}
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('capabilities.tokenizer')}
              >
                Arch{renderSortIndicator('capabilities.tokenizer')}
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider">
                Capabilities
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {sortedModels.map((model) => (
              <tr 
                key={model.id}
                className={`${!model.is_available ? (darkMode ? 'opacity-50' : 'bg-gray-50 opacity-70') : ''} hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
              >
                {/* Details button - now first column */}
                <td className="px-2 py-2 whitespace-nowrap text-xs">
                  <button
                    onClick={() => setSelectedModelDetail(model)}
                    className={`text-xs px-2 py-0.5 rounded ${
                      darkMode ? 'bg-gray-700 hover:bg-gray-600 text-blue-300' : 'bg-gray-100 hover:bg-gray-200 text-blue-600'
                    }`}
                  >
                    Details
                  </button>
                </td>
                {/* Status indicator - moved to second column */}
                <td className="px-2 py-2 whitespace-nowrap text-xs">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    model.is_available 
                      ? 'bg-green-500' 
                      : 'bg-red-500'
                  }`} title={model.is_available ? 'Available' : 'Unavailable'}></span>
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-xs">
                  <div className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-1 ${getProviderColor(model.provider)}`}></div>
                    <span>{model.provider}</span>
                  </div>
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-xs font-medium truncate max-w-[220px]"> {/* Further increased max width */}
                  {model.name}
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-xs">
                  {model.context_length.toLocaleString()}
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-xs">
                  {formatDollars(model.prompt_cost_per_1k)}
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-xs">
                  {formatDollars(model.completion_cost_per_1k)}
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-xs">
                  <div className="flex flex-col">
                    <span className="truncate max-w-[80px]" title={model.capabilities.tokenizer || 'Unknown'}>
                      {model.capabilities.tokenizer || '-'}
                    </span>
                    <span className="text-xs text-gray-500 truncate max-w-[80px]" title={model.capabilities.instruct_type || 'Unknown'}>
                      {model.capabilities.instruct_type || '-'}
                    </span>
                  </div>
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-xs">
                  <div className="flex flex-wrap gap-1">
                    {/* Show only the most important capabilities */}
                    {['vision', 'tools', 'json_mode'].map(key => (
                      model.capabilities[key] && (
                        <span 
                          key={key}
                          className={`text-xs px-1 py-0.5 rounded ${
                            darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {key.replace('_', ' ')}
                        </span>
                      )
                    ))}
                    {/* Show first input modality if available */}
                    {model.capabilities.input_modalities && 
                     model.capabilities.input_modalities.length > 0 && 
                     model.capabilities.input_modalities[0] !== 'text' && (
                      <span 
                        className={`text-xs px-1 py-0.5 rounded ${
                          darkMode ? 'bg-purple-900 text-purple-100' : 'bg-purple-100 text-purple-800'
                        }`}
                      >
                        {model.capabilities.input_modalities[0]}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  const renderTab = () => {
    switch (activeTab) {
      case 'comparison':
        return (
          <div className="mt-6">
            <h2 className="text-xl font-semibold mb-4">Model Comparison</h2>
            <ModelComparison 
              inputText="This is a sample text to estimate token usage. You can edit this to see how different inputs affect costs."
              darkMode={darkMode}
            />
          </div>
        );
      case 'trends':
        return (
          <div className="mt-6">
            <h2 className="text-xl font-semibold mb-4">Model Trends</h2>
            <ModelPriceTrends darkMode={darkMode} showPrices={true} />
          </div>
        );
      case 'sync':
        return (
          <div className="mt-6">
            <h2 className="text-xl font-semibold mb-4">Model Sync Status</h2>
            <div className="mb-8">
              <ModelSyncStatus darkMode={darkMode} isAdmin={false} />
            </div>
          </div>
        );
      case 'prices':
        return (
          <div className="mt-6">
            <h2 className="text-xl font-semibold mb-4">Model Price Trends</h2>
            <div className="mb-8">
              <ModelPriceTrends darkMode={darkMode} days={30} showPrices={true} />
            </div>
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Price Ranges</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className={`p-4 rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <h2 className="text-lg font-semibold mb-2">Input Price Range</h2>
                  <div className="text-2xl font-bold">
                    {validPriceModels.length > 0 ? formatDollars(Math.min(...validPriceModels.map(m => m.prompt_cost_per_1k))) : '$0'} - {validPriceModels.length > 0 ? formatDollars(Math.max(...validPriceModels.map(m => m.prompt_cost_per_1k))) : '$0'}
                  </div>
                  <div className="text-sm mt-2">Per 1M tokens</div>
                </div>
                
                <div className={`p-4 rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <h2 className="text-lg font-semibold mb-2">Output Price Range</h2>
                  <div className="text-2xl font-bold">
                    {validPriceModels.length > 0 ? formatDollars(Math.min(...validPriceModels.map(m => m.completion_cost_per_1k))) : '$0'} - {validPriceModels.length > 0 ? formatDollars(Math.max(...validPriceModels.map(m => m.completion_cost_per_1k))) : '$0'}
                  </div>
                  <div className="text-sm mt-2">Per 1M tokens</div>
                </div>
                
                <div className={`p-4 rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                  <h2 className="text-lg font-semibold mb-2">Average Input Price</h2>
                  <div className="text-2xl font-bold">
                    {validPriceModels.length > 0 ? formatDollars(validPriceModels.reduce((sum, model) => sum + model.prompt_cost_per_1k, 0) / validPriceModels.length) : '$0'}
                  </div>
                  <div className="text-sm mt-2">Per 1M tokens</div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'overview':
      default:
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Context Range Card */}
              <div className={`p-3 rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h2 className="text-base font-semibold mb-1">Context Range</h2>
                <div className="text-2xl font-bold mb-2">
                  {contextRange[0].toLocaleString()} - {contextRange[1].toLocaleString()}
                </div>
                <div className="text-xs mb-2">Tokens across all models</div>
                
                <div className="px-2 py-4">
                  {contextMinMax[1] > contextMinMax[0] && 
                   contextRange[0] >= contextMinMax[0] && 
                   contextRange[1] <= contextMinMax[1] && 
                   contextRange[0] <= contextRange[1] ? (
                    <Range
                      step={1000}
                      min={contextMinMax[0]}
                      max={contextMinMax[1]}
                      values={[
                        // Ensure values are aligned with step
                        Math.round((contextRange[0] - contextMinMax[0]) / 1000) * 1000 + contextMinMax[0],
                        Math.round((contextRange[1] - contextMinMax[0]) / 1000) * 1000 + contextMinMax[0]
                      ]}
                      onChange={(values) => {
                        setContextRange([values[0], values[1]]);
                        setIsFilterActive(true);
                      }}
                      renderTrack={({ props, children }) => (
                        <div
                          {...props}
                          className={`h-1 w-full rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                        >
                          <div
                            className={`h-1 rounded-full ${darkMode ? 'bg-blue-600' : 'bg-blue-500'}`}
                            style={{
                              position: 'absolute',
                              left: `${(contextRange[0] - contextMinMax[0]) / (contextMinMax[1] - contextMinMax[0]) * 100}%`,
                              width: `${(contextRange[1] - contextRange[0]) / (contextMinMax[1] - contextMinMax[0]) * 100}%`
                            }}
                          />
                          {children}
                        </div>
                      )}
                      renderThumb={({ props: { key, ...thumbProps } }) => (
                        <div
                          key={key}
                          {...thumbProps}
                          className={`w-4 h-4 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            darkMode ? 'bg-blue-500' : 'bg-blue-600'
                          }`}
                        />
                      )}
                    />
                  ) : (
                    <div className="text-center text-sm text-gray-500">
                      Loading context range data...
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between text-xs mt-1">
                  <span>{contextMinMax[0].toLocaleString()}</span>
                  <span>{contextMinMax[1].toLocaleString()}</span>
                </div>
                
                <div className="mt-4">
                  <form onSubmit={handleContextRangeSubmit} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={contextInputMin}
                      onChange={handleContextMinChange}
                      className={`w-24 px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'
                      } border`}
                      placeholder="Min"
                    />
                    <span>-</span>
                    <input
                      type="text"
                      value={contextInputMax}
                      onChange={handleContextMaxChange}
                      className={`w-24 px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'
                      } border`}
                      placeholder="Max"
                    />
                    <button
                      type="submit"
                      className={`px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                      } text-white`}
                    >
                      Apply
                    </button>
                  </form>
                </div>
              </div>
              
              {/* Available Models Card */}
              <div className={`p-3 rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h2 className="text-base font-semibold mb-1">Available Models</h2>
                <div className="text-2xl font-bold">
                  {sortedModels.length} / {models.length}
                </div>
                <div className="text-xs mt-1">From {new Set(sortedModels.map(m => m.provider)).size} providers</div>
                
                {isFilterActive && (
                  <button
                    onClick={resetAllFilters}
                    className={`mt-4 px-3 py-1 text-xs rounded ${
                      darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                    } text-white`}
                  >
                    Reset All Filters
                  </button>
                )}
              </div>
              
              {/* Price Range Card */}
              <div className={`p-3 rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h2 className="text-base font-semibold mb-1">Price Range (Input)</h2>
                <div className="text-2xl font-bold mb-2">
                  {formatDollars(priceRange[0])} - {formatDollars(priceRange[1])}
                </div>
                <div className="text-xs mb-2">Per 1M tokens</div>
                
                <div className="px-2 py-4">
                  {priceMinMax[1] > priceMinMax[0] && 
                   priceRange[0] >= priceMinMax[0] && 
                   priceRange[1] <= priceMinMax[1] && 
                   priceRange[0] <= priceRange[1] ? (
                    <Range
                      step={1}
                      min={priceMinMax[0]}
                      max={priceMinMax[1]}
                      values={[
                        // Ensure values are aligned with step
                        Math.floor(priceRange[0]),
                        Math.ceil(priceRange[1])
                      ]}
                      onChange={(values) => {
                        setPriceRange([values[0], values[1]]);
                        setIsFilterActive(true);
                      }}
                      renderTrack={({ props, children }) => (
                        <div
                          {...props}
                          className={`h-1 w-full rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                        >
                          <div
                            className={`h-1 rounded-full ${darkMode ? 'bg-blue-600' : 'bg-blue-500'}`}
                            style={{
                              position: 'absolute',
                              left: `${(priceRange[0] - priceMinMax[0]) / (priceMinMax[1] - priceMinMax[0]) * 100}%`,
                              width: `${(priceRange[1] - priceRange[0]) / (priceMinMax[1] - priceMinMax[0]) * 100}%`
                            }}
                          />
                          {children}
                        </div>
                      )}
                      renderThumb={({ props: { key, ...thumbProps } }) => (
                        <div
                          key={key}
                          {...thumbProps}
                          className={`w-4 h-4 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            darkMode ? 'bg-blue-500' : 'bg-blue-600'
                          }`}
                        />
                      )}
                    />
                  ) : (
                    <div className="text-center text-sm text-gray-500">
                      Loading price range data...
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between text-xs mt-1">
                  <span>{formatDollars(priceMinMax[0])}</span>
                  <span>{formatDollars(priceMinMax[1])}</span>
                </div>
                
                <div className="mt-4">
                  <form onSubmit={handlePriceRangeSubmit} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={priceInputMin}
                      onChange={handlePriceMinChange}
                      className={`w-24 px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'
                      } border`}
                      placeholder="$0.00"
                      pattern="^\$?\d*\.?\d*$"  // Allow dollar sign and decimal numbers
                    />
                    <span>-</span>
                    <input
                      type="text"
                      value={priceInputMax}
                      onChange={handlePriceMaxChange}
                      className={`w-24 px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'
                      } border`}
                      placeholder="$0.00"
                      pattern="^\$?\d*\.?\d*$"  // Allow dollar sign and decimal numbers
                    />
                    <button
                      type="submit"
                      className={`px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                      } text-white`}
                    >
                      Apply
                    </button>
                  </form>
                </div>
              </div>
              
              {/* Price Range (Output) Card */}
              <div className={`p-3 rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h2 className="text-base font-semibold mb-1">Price Range (Output)</h2>
                <div className="text-2xl font-bold mb-2">
                  {formatDollars(outputPriceRange[0])} - {formatDollars(outputPriceRange[1])}
                </div>
                <div className="text-xs mb-2">Per 1M tokens</div>
                
                <div className="px-2 py-4">
                  {outputPriceMinMax[1] > outputPriceMinMax[0] && 
                   outputPriceRange[0] >= outputPriceMinMax[0] && 
                   outputPriceRange[1] <= outputPriceMinMax[1] && 
                   outputPriceRange[0] <= outputPriceRange[1] ? (
                    <Range
                      step={1}
                      min={outputPriceMinMax[0]}
                      max={outputPriceMinMax[1]}
                      values={[
                        // Ensure values are aligned with step
                        Math.floor(outputPriceRange[0]),
                        Math.ceil(outputPriceRange[1])
                      ]}
                      onChange={(values) => {
                        setOutputPriceRange([values[0], values[1]]);
                        setIsFilterActive(true);
                      }}
                      renderTrack={({ props, children }) => (
                        <div
                          {...props}
                          className={`h-1 w-full rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                        >
                          <div
                            className={`h-1 rounded-full ${darkMode ? 'bg-blue-600' : 'bg-blue-500'}`}
                            style={{
                              position: 'absolute',
                              left: `${(outputPriceRange[0] - outputPriceMinMax[0]) / (outputPriceMinMax[1] - outputPriceMinMax[0]) * 100}%`,
                              width: `${(outputPriceRange[1] - outputPriceRange[0]) / (outputPriceMinMax[1] - outputPriceMinMax[0]) * 100}%`
                            }}
                          />
                          {children}
                        </div>
                      )}
                      renderThumb={({ props: { key, ...thumbProps } }) => (
                        <div
                          key={key}
                          {...thumbProps}
                          className={`w-4 h-4 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            darkMode ? 'bg-blue-500' : 'bg-blue-600'
                          }`}
                        />
                      )}
                    />
                  ) : (
                    <div className="text-center text-sm text-gray-500">
                      Loading price range data...
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between text-xs mt-1">
                  <span>{formatDollars(outputPriceMinMax[0])}</span>
                  <span>{formatDollars(outputPriceMinMax[1])}</span>
                </div>
                
                <div className="mt-4">
                  <form onSubmit={handleOutputPriceRangeSubmit} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={outputPriceInputMin}
                      onChange={handleOutputPriceMinChange}
                      className={`w-24 px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'
                      } border`}
                      placeholder="$0.00"
                      pattern="^\$?\d*\.?\d*$"  // Allow dollar sign and decimal numbers
                    />
                    <span>-</span>
                    <input
                      type="text"
                      value={outputPriceInputMax}
                      onChange={handleOutputPriceMaxChange}
                      className={`w-24 px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'
                      } border`}
                      placeholder="$0.00"
                      pattern="^\$?\d*\.?\d*$"  // Allow dollar sign and decimal numbers
                    />
                    <button
                      type="submit"
                      className={`px-2 py-1 text-xs rounded ${
                        darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                      } text-white`}
                    >
                      Apply
                    </button>
                  </form>
                </div>
              </div>
            </div>
            
            
            {/* Add a new grid for search, capabilities, and provider filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Search box */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
                <h3 className="text-sm font-medium mb-2">Search Models:</h3>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name or description..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setIsFilterActive(e.target.value !== '');
                    }}
                    className={`w-full px-4 py-2 pl-10 rounded-lg ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500'
                    } border focus:outline-none focus:ring-2 ${
                      darkMode ? 'focus:ring-blue-600' : 'focus:ring-blue-500'
                    }`}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Capability filter */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
                <div 
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => setIsCapabilitySectionExpanded(!isCapabilitySectionExpanded)}
                >
                  <h3 className="text-sm font-medium">Filter by Capabilities:</h3>
                  <button className="text-gray-500 hover:text-gray-700">
                    {isCapabilitySectionExpanded ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
                
                {isCapabilitySectionExpanded && (
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-2">
                      {['vision', 'function_calling', 'json_mode', 'streaming'].map(capability => (
                        <button
                          key={capability}
                          onClick={() => {
                            // Toggle capability filter
                            if (capabilityFilters.includes(capability)) {
                              setCapabilityFilters(capabilityFilters.filter(c => c !== capability));
                            } else {
                              setCapabilityFilters([...capabilityFilters, capability]);
                            }
                            setIsFilterActive(true);
                          }}
                          className={`px-3 py-1 text-xs rounded-full ${
                            capabilityFilters.includes(capability)
                              ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                              : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300')
                          }`}
                        >
                          {capability.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                    {capabilityFilters.length > 0 && (
                      <div className="mt-2 text-xs text-gray-500">
                        <span className="font-medium">Note:</span> Shows models with <strong>all</strong> selected capabilities.
                      </div>
                    )}
                    {capabilityFilters.length > 0 && (
                      <button
                        onClick={resetCapabilityFilters}
                        className={`mt-2 text-xs px-2 py-1 rounded ${
                          darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Provider filter */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
                <div 
                  className="flex justify-between items-center cursor-pointer" 
                  onClick={() => setIsProviderSectionExpanded(!isProviderSectionExpanded)}
                >
                  <h3 className="text-sm font-medium">Filter by Provider:</h3>
                  <button className="text-gray-500 hover:text-gray-700">
                    {isProviderSectionExpanded ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
                
                {isProviderSectionExpanded && (
                  <div className="mt-2">
                    <select 
                      value={filterProvider}
                      onChange={(e) => {
                        setFilterProvider(e.target.value);
                        setIsFilterActive(e.target.value !== 'all');
                      }}
                      className={`w-full px-3 py-2 rounded text-sm ${
                        darkMode 
                          ? 'bg-gray-700 text-white border-gray-600' 
                          : 'bg-gray-50 text-gray-800 border-gray-300'
                      } border`}
                    >
                      {providers.map(provider => (
                        <option key={provider} value={provider}>
                          {provider === 'all' ? 'All Providers' : provider}
                        </option>
                      ))}
                    </select>
                    
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      {providers.filter(p => p !== 'all').slice(0, 6).map(provider => {
                        const count = models.filter(m => m.provider === provider).length;
                        return (
                          <div key={provider} className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-1 ${getProviderColor(provider)}`}></div>
                            <span>{provider}: {count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mb-8">
              
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold">Available Models</h2>
                  {models.length > 0 && (
                    <div className="text-xs text-gray-500">
                      Last updated: {new Date(models[0]?.updated_at || Date.now()).toLocaleString()}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-4">
                  {/* View toggle buttons */}
                  <div className="flex rounded-md shadow-sm" role="group">
                    <button
                      type="button"
                      onClick={() => setViewMode('grid')}
                      className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                        viewMode === 'grid'
                          ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                          : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
                      }`}
                    >
                      Grid
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode('list')}
                      className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                        viewMode === 'list'
                          ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                          : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
                      }`}
                    >
                      List
                    </button>
                  </div>
                  
                  {/* Export button - opens the modal */}
                  <button
                    onClick={() => setShowExportModal(true)}
                    className={`px-4 py-2 text-sm font-medium rounded flex items-center ${
                      darkMode 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                    title="Export currently filtered model data"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Export Selected...
                  </button>
                </div>
              </div>
              
              {loading ? (
                <div className="flex justify-center p-8"><Spinner size="lg" /></div>
              ) : sortedModels.length === 0 ? (
                <div className={`p-8 text-center rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
                  <p className="text-lg font-medium mb-2">No models match your filters</p>
                  <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Try adjusting your filters or clearing them to see more models.
                  </p>
                  <button
                    onClick={resetAllFilters}
                    className={`px-4 py-2 rounded ${
                      darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                    } text-white`}
                  >
                    Clear All Filters
                  </button>
                </div>
              ) : (
                viewMode === 'grid' ? renderGridView() : renderListView()
              )}
            </div>
          </>
        );
    }
  };
  
  return (
    <div className={`w-full ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold">Model Management</h1>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded ${
              activeTab === 'overview'
                ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('sync')}
            className={`px-4 py-2 rounded ${
              activeTab === 'sync'
                ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
            }`}
          >
            Sync Status
          </button>
          <button
            onClick={() => setActiveTab('prices')}
            className={`px-4 py-2 rounded ${
              activeTab === 'prices'
                ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
            }`}
          >
            Price Trends
          </button>
          <button
            onClick={() => setActiveTab('comparison')}
            className={`px-4 py-2 rounded ${
              activeTab === 'comparison'
                ? (darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
            }`}
          >
            Compare
          </button>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {hasNegativePrices && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-4">
          <strong>Warning:</strong> Negative prices detected in model data. This may indicate an issue with the data import process.
        </div>
      )}
      
      {renderTab()}
      
      {/* Model Detail Modal */}
      {selectedModelDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Header */}
            <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} sticky top-0 ${darkMode ? 'bg-gray-800' : 'bg-white'} z-10`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-2 ${getProviderColor(selectedModelDetail.provider)}`}></div>
                    <h2 className="text-xl font-bold">{selectedModelDetail.name}</h2>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                      selectedModelDetail.is_available 
                        ? (darkMode ? 'bg-green-900 text-green-100' : 'bg-green-100 text-green-800')
                        : (darkMode ? 'bg-red-900 text-red-100' : 'bg-red-100 text-red-800')
                    }`}>
                      {selectedModelDetail.is_available ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{selectedModelDetail.id}</p>
                </div>
                <button
                  onClick={() => setSelectedModelDetail(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              {/* Description */}
              <div className="mb-6">
                <h3 className="text-sm font-medium mb-2">Description</h3>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {selectedModelDetail.description || 'No description available'}
                </p>
              </div>
              
              {/* Key specs in a grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <h4 className="text-xs uppercase font-semibold mb-1 text-gray-500">Context Length</h4>
                  <p className="text-xl font-bold">{selectedModelDetail.context_length.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">tokens</p>
                </div>
                
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <h4 className="text-xs uppercase font-semibold mb-1 text-gray-500">Input Price</h4>
                  <p className="text-xl font-bold">{formatDollars(selectedModelDetail.prompt_cost_per_1k)}</p>
                  <p className="text-xs text-gray-500">per 1M tokens</p>
                </div>
                
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <h4 className="text-xs uppercase font-semibold mb-1 text-gray-500">Output Price</h4>
                  <p className="text-xl font-bold">{formatDollars(selectedModelDetail.completion_cost_per_1k)}</p>
                  <p className="text-xs text-gray-500">per 1M tokens</p>
                </div>
              </div>
              
              {/* Architecture & Capabilities */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Architecture */}
                <div>
                  <h3 className="text-sm font-medium mb-3">Architecture</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Tokenizer</span>
                      <span className="font-medium">{selectedModelDetail.capabilities.tokenizer || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Instruct Type</span>
                      <span className="font-medium">{selectedModelDetail.capabilities.instruct_type || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Modality</span>
                      <span className="font-medium">{selectedModelDetail.capabilities.modality || 'Unknown'}</span>
                    </div>
                  </div>
                </div>
                
                {/* Capabilities */}
                <div>
                  <h3 className="text-sm font-medium mb-3">Capabilities</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(selectedModelDetail.capabilities)
                      .filter(([key, value]) => 
                        typeof value === 'boolean' && value === true && 
                        !['input_modalities', 'output_modalities', 'tokenizer', 'instruct_type', 'modality'].includes(key)
                      )
                      .map(([key]) => (
                        <span 
                          key={key}
                          className={`text-xs px-2 py-1 rounded ${
                            darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {key.replace(/_/g, ' ')}
                        </span>
                      ))
                    }
                  </div>
                </div>
              </div>
              
              {/* Modalities */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Input Modalities */}
                <div>
                  <h3 className="text-sm font-medium mb-3">Input Modalities</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedModelDetail.capabilities.input_modalities && 
                     selectedModelDetail.capabilities.input_modalities.length > 0 ? (
                      selectedModelDetail.capabilities.input_modalities.map(modality => (
                        <span 
                          key={modality}
                          className={`text-xs px-2 py-1 rounded ${
                            darkMode ? 'bg-purple-900 text-purple-100' : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {modality}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">No input modalities specified</span>
                    )}
                  </div>
                </div>
                
                {/* Output Modalities */}
                <div>
                  <h3 className="text-sm font-medium mb-3">Output Modalities</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedModelDetail.capabilities.output_modalities && 
                     selectedModelDetail.capabilities.output_modalities.length > 0 ? (
                      selectedModelDetail.capabilities.output_modalities.map(modality => (
                        <span 
                          key={modality}
                          className={`text-xs px-2 py-1 rounded ${
                            darkMode ? 'bg-indigo-900 text-indigo-100' : 'bg-indigo-100 text-indigo-800'
                          }`}
                        >
                          {modality}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-gray-500">No output modalities specified</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Advanced Information */}
              <div className="mb-6">
                <h3 className="text-sm font-medium mb-3">Advanced Information</h3>
                
                {/* Request Limits */}
                {selectedModelDetail.meta_data && selectedModelDetail.meta_data.per_request_limits && (
                  <div className="mb-4">
                    <h4 className="text-xs uppercase font-semibold mb-1 text-gray-500">Request Limits</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Max Prompt Tokens</span>
                        <p className="font-medium">
                          {selectedModelDetail.meta_data.per_request_limits.prompt_tokens?.toLocaleString() || 'Not specified'}
                        </p>
                      </div>
                      <div>
                        <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Max Completion Tokens</span>
                        <p className="font-medium">
                          {selectedModelDetail.meta_data.per_request_limits.completion_tokens?.toLocaleString() || 'Not specified'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Supported Parameters */}
                {selectedModelDetail.meta_data && selectedModelDetail.meta_data.supported_parameters && (
                  <div className="mb-4">
                    <h4 className="text-xs uppercase font-semibold mb-1 text-gray-500">Supported Parameters</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedModelDetail.meta_data.supported_parameters.map(param => (
                        <span 
                          key={param}
                          className={`text-xs px-2 py-1 rounded ${
                            darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {param}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Default Parameters */}
                {selectedModelDetail.meta_data && selectedModelDetail.meta_data.default_parameters && 
                 Object.keys(selectedModelDetail.meta_data.default_parameters).length > 0 && (
                  <div>
                    <h4 className="text-xs uppercase font-semibold mb-1 text-gray-500">Default Parameters</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(selectedModelDetail.meta_data.default_parameters).map(([key, value]) => (
                        <div key={key}>
                          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{key}</span>
                          <p className="font-medium">{value?.toString() || 'N/A'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Metadata */}
              <div>
                <h3 className="text-sm font-medium mb-3">Additional Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {selectedModelDetail.meta_data && selectedModelDetail.meta_data.canonical_slug && (
                    <div>
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Canonical Slug</span>
                      <p className="font-medium">{selectedModelDetail.meta_data.canonical_slug}</p>
                    </div>
                  )}
                  {selectedModelDetail.meta_data && selectedModelDetail.meta_data.hugging_face_id && (
                    <div>
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Hugging Face ID</span>
                      <p className="font-medium">{selectedModelDetail.meta_data.hugging_face_id}</p>
                    </div>
                  )}
                  {selectedModelDetail.meta_data && selectedModelDetail.meta_data.created && (
                    <div>
                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Created</span>
                      <p className="font-medium">{new Date(selectedModelDetail.meta_data.created * 1000).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} sticky bottom-0 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="flex justify-end">
                <button
                  onClick={() => setSelectedModelDetail(null)}
                  className={`px-4 py-2 rounded ${
                    darkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-lg p-6 max-w-md w-full ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className="text-lg font-semibold mb-4">Export Selected Models</h3>
            
            <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Export {sortedModels.length} selected models to the format of your choice.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Export Format</label>
              <div className="relative">
                <select
                  className={`block w-full px-3 py-2 rounded border ${
                    darkMode 
                      ? 'bg-gray-700 text-white border-gray-600' 
                      : 'bg-white text-gray-800 border-gray-300'
                  }`}
                  defaultValue="csv"
                >
                  <option value="csv">CSV (Comma Separated Values)</option>
                  <option value="xlsx" disabled className="text-gray-500">XLSX (Excel) - Coming Soon</option>
                  <option value="md" disabled className="text-gray-500">MD (Markdown) - Coming Soon</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowExportModal(false)}
                className={`px-4 py-2 rounded ${
                  darkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => handleExport('csv')}
                className={`px-4 py-2 rounded ${
                  darkMode 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelDashboard;
